import { useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PiX } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';
import { PageHeader } from 'src/modules/shared/components/molecules/page-header';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { useEmployeeMetadata } from 'src/modules/shared/hooks/api/automations/employees/employees';
import { useEmployeeExecutions } from 'src/modules/shared/hooks/api/automations/employees/executions';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { ExecutionDateRangePicker } from 'src/modules/automations/components/molecules/employees/executions/execution-date-range-picker';
import EmployeeExecutionsPanel from 'src/modules/automations/components/organisms/employees/executions/executions-panel';
import {
  executionSearchSchema,
  executionsSearchSchema,
  type ExecutionsSearch,
} from 'src/modules/automations/schemas/employees/executions.schema';
import {
  normalizeExecutionDateToken,
  toExecutionDateQuery,
} from 'src/modules/automations/utils/executions/execution-date-filter.util';
import { Route as EmployeesEmployeeIdExecutionsExecutionIdRoute } from 'src/routes/employees/$employeeId/executions/$executionId';
import { Route as EmployeesEmployeeIdExecutionsIndexRoute } from 'src/routes/employees/$employeeId/executions/index';
import { Route as EmployeesIndexRoute } from 'src/routes/employees/index';
import { employeesSearchSchema } from 'src/modules/automations/schemas/employees/employees.schema';

interface EmployeeExecutionsPageProps {
  /** AI Agent whose execution history is displayed. */
  employeeId: string;
}

/** AI Agent execution index with shared page and date-filter primitives. */
export const EmployeeExecutionsPage: React.FC<EmployeeExecutionsPageProps> = ({
  employeeId,
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { employee } = useEmployeeMetadata(employeeId);
  const search = useSearch({
    from: EmployeesEmployeeIdExecutionsIndexRoute.id,
    select: (searchParams: ExecutionsSearch) => searchParams,
  });
  const dateRange = useMemo(
    () => ({
      from: normalizeExecutionDateToken(search.from),
      to: normalizeExecutionDateToken(search.to),
    }),
    [search.from, search.to]
  );
  const queryParams = useMemo(
    () => toExecutionDateQuery(dateRange),
    [dateRange]
  );
  const {
    executions,
    loading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useEmployeeExecutions(employeeId, queryParams);
  const agentName =
    employee?.name ?? t('automations.aiAgents.executions.unknownAgent');
  const hasDateFilter = Boolean(dateRange.from && dateRange.to);

  const updateDateRange = (from?: string, to?: string) => {
    void navigate({
      to: EmployeesEmployeeIdExecutionsIndexRoute.to,
      params: { employeeId },
      search: (previous) => ({
        ...executionsSearchSchema.parse(previous),
        from,
        to,
      }),
    });
  };

  const handleExecutionSelect = (executionId: string) => {
    void navigate({
      to: EmployeesEmployeeIdExecutionsExecutionIdRoute.to,
      params: { employeeId, executionId },
      search: (previous) => executionSearchSchema.parse(previous),
    });
  };

  const handleBack = () => {
    void navigate({
      to: EmployeesIndexRoute.to,
      search: (previous) => employeesSearchSchema.parse(previous),
    });
  };

  return (
    <div className="flex h-full flex-col">
      <Panel className="flex h-full min-h-0 flex-col">
        <PageHeader
          title={t('automations.aiAgents.executions.title')}
          subtitle={t('automations.aiAgents.executions.description', {
            agentName,
          })}
          onBack={handleBack}
          backLabel={t('automations.aiAgents.executions.back')}
        />

        <PageFiltersBar contentClassName="sm:items-center">
          <ExecutionDateRangePicker
            value={dateRange}
            onApply={(range) => updateDateRange(range.from, range.to)}
          />
          {hasDateFilter ? (
            <Button
              type="button"
              variant="secondary"
              appearance="ghost"
              size="sm"
              onClick={() => updateDateRange()}
              className="w-full gap-2 sm:w-auto"
            >
              <PiX className="h-4 w-4" />
              {t('automations.aiAgents.actions.clearDates')}
            </Button>
          ) : null}
        </PageFiltersBar>

        {error ? (
          <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-50 p-4">
            <ErrorState
              title={t('automations.aiAgents.executions.errorTitle')}
              description={t(
                'automations.aiAgents.executions.errorDescription'
              )}
              onAction={() => void refetch()}
              actionContent={t('automations.aiAgents.actions.retry')}
            />
          </div>
        ) : (
          <EmployeeExecutionsPanel
            executions={executions}
            onExecutionSelect={handleExecutionSelect}
            onLoadMore={() => void fetchNextPage()}
            hasMore={Boolean(hasNextPage)}
            isLoadingMore={isFetchingNextPage}
            isInitialLoading={loading && executions.length === 0}
          />
        )}
      </Panel>
    </div>
  );
};
